import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { history } from "../..";
import agent from '../api/agents';
import CookieHelper from '../helpers/cookieHelper';
import { UserFormValues } from "../models/user";
import { store } from "./store";
import { Auth } from "../models/auth";
import { getTokenPermissions, readJwtPayload } from '../helpers/permission-claims';

const _cookieHelper = new CookieHelper();

export default class AuthStore {
    token: string | null = _cookieHelper.get('jsonWebToken') || null;
    refreshToken: string | null = _cookieHelper.get('refreshToken') || null;
    auth: Auth | null = null;
    refreshTokenTimeout: any;

    constructor() {
        makeAutoObservable(this);

        reaction(
            () => this.token,
            token => {
                this.auth = null;
                if (token) {
                    _cookieHelper.set('jsonWebToken', token);
                }
                else {
                    _cookieHelper.delete('jsonWebToken');
                }
            }
        );

        reaction(
            () => this.refreshToken,
            token => {
                if (token) {
                    _cookieHelper.set('refreshToken', token);
                }
                else {
                    _cookieHelper.delete('refreshToken');
                }
            }
        );
    }

    setToken = (token: string | null) => {
        this.token = token;
        this.auth = null;
    }

    setRefreshToken = (refreshToken: string | null) => {
        this.refreshToken = refreshToken;
    }

    login = async (creds: UserFormValues) => {
        try {
            const accessToken = await agent.Account.login(creds);
            this.setToken(accessToken.jsonWebToken!);
            this.setRefreshToken(accessToken.refreshToken!);
            const user = await agent.Account.current();
            this.startRefreshTokenTimer();
            runInAction(() => {
                store.userStore.user = user;
                store.userStore.setDisplayName(user);
                store.commonStore.isCollapseSideBar = false;
                history.push('/applications');
            });
        } catch (e) {
            console.error(e);
        }
    }

    hasPermission = (permission: string | undefined) => {
        if (!this.auth) this.setUserAuth();

        if (permission) {
            return this.auth?.permissions.includes(permission) ?? false;
        }

        return false;
    }

    logout = () => {
        this.setToken(null);
        this.setRefreshToken(null);
        _cookieHelper.delete('jsonWebToken');
        _cookieHelper.delete('refreshToken');
        store.userStore.user = null;
        this.auth = null;
        history.push('/');
    }

    setUserAuth = () => {
        this.auth = this.token ? { permissions: getTokenPermissions(this.token) } : null;
        return this.auth;
    };

    refreshTheToken = async () => {
        try {
            const accessToken = await agent.Account.refreshToken({
                jsonWebToken: this.token!,
                refreshToken: this.refreshToken!
            });

            runInAction(() => {
                this.setToken(accessToken.jsonWebToken!);
                this.setRefreshToken(accessToken.refreshToken!);
                this.startRefreshTokenTimer();
            })
        } catch (error) {
            console.log(error);
        }
    }

    startRefreshTokenTimer() {
        if (this.token) {
            const jwtToken = readJwtPayload(this.token);
            if (!jwtToken || typeof jwtToken.exp !== 'number') return;
            this.stopRefreshTokenTimer();
            const expires = new Date(jwtToken.exp * 1000);
            const timeout = expires.getTime() - Date.now() - (30 * 1000);
            this.refreshTokenTimeout = setTimeout(this.refreshTheToken, timeout);
        }
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }
}
